import React, { useEffect, useState } from "react";
import StaffNavBar from "../components/StaffNavBar";
import {
  AdminPageBody,
  AdminPanel,
  AdminLoadingState,
  adminButton,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faKey,
  faUpload,
  faTrash,
  faXmark,
  faEye,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import { useStaffExamPins } from "../store/staff";
import {useConfirmDialog} from "../components/ConfirmDialogProvider";

const STATUSES = ["", "AVAILABLE", "SOLD"];
const TYPES = ["", "WAEC", "NECO", "NABTEB"];
const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";

const StaffExamPins = () => {
  const confirm = useConfirmDialog();
  const {
    staffExamPins,
    staffExamPinsLoading,
    staffExamPinsError,
    staffExamPinsTotal,
    staffExamPinsPages,
    staffExamPinStats,
    staffExamPinDetail,
    staffExamPinDetailLoading,
    staffExamPinDetailError,
    staffExamPinDeliveries,
    staffExamPinDeliveriesLoading,
    staffExamPinDeliveriesError,
    staffExamPinDeliveriesTotal,
    staffExamPinDeliveriesPages,
    staffExamPinMutationLoading,
    staffExamPinMutationError,
    fetchStaffExamPins,
    fetchStaffExamPin,
    fetchStaffExamPinStats,
    fetchStaffExamPinDeliveries,
    uploadStaffExamPins,
    deleteStaffExamPin,
  } = useStaffExamPins();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [examType, setExamType] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadText, setUploadText] = useState("");
  const [uploadType, setUploadType] = useState("WAEC");
  const [detailOpen, setDetailOpen] = useState(false);
  const [deliveriesOpen, setDeliveriesOpen] = useState(false);
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [deliveryStatus, setDeliveryStatus] = useState("");

  const params = {
    page,
    limit: 10,
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(examType ? { examType } : {}),
  };

  // GET /api/staff/exam-pins  (+ stats)
  useEffect(() => {
    fetchStaffExamPins(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, status, examType]);

  useEffect(() => {
    fetchStaffExamPinStats(examType ? { examType } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examType]);

  // GET /api/staff/exam-pins/deliveries — loaded while the delivery log is open.
  useEffect(() => {
    if (!deliveriesOpen) return;
    fetchStaffExamPinDeliveries({
      page: deliveryPage,
      limit: 10,
      ...(deliveryStatus ? { status: deliveryStatus } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveriesOpen, deliveryPage, deliveryStatus]);

  const reload = () => {
    fetchStaffExamPins(params);
    fetchStaffExamPinStats(examType ? { examType } : {});
  };

  const openDetail = async (id) => {
    setDetailOpen(true);
    await fetchStaffExamPin(id);
  };

  const openDeliveries = () => {
    setDeliveryPage(1);
    setDeliveriesOpen(true);
  };

  const handleUpload = async () => {
    const pins = uploadText
      .split(/[\n,]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (pins.length === 0) return;
    const res = await uploadStaffExamPins({ pins, examType: uploadType });
    if (res.success) {
      setUploadOpen(false);
      setUploadText("");
      reload();
    }
  };

  const stats = staffExamPinStats || {};
  const statCards = [
    { label: "Available", value: stats.available },
    { label: "Sold", value: stats.sold },
    { label: "Total", value: stats.total },
  ];

  return (
    <div>
      <StaffNavBar title="Exam PINs" context="Upload and manage exam PIN inventory." />
      <AdminPageBody>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {statCards.map((s) => (
            <AdminPanel key={s.label} className="p-5">
              <div className="mb-1 text-2xl font-bold text-slate-900">
                {s.value != null ? Number(s.value).toLocaleString() : "—"}
              </div>
              <div className="text-sm text-slate-500">{s.label}</div>
            </AdminPanel>
          ))}
        </div>

        <AdminPanel className="overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
            <input
              className={`${inputCls} max-w-xs`}
              placeholder="Search pins…"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
            <select
              className={`${inputCls} max-w-[160px]`}
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s || "All statuses"}
                </option>
              ))}
            </select>
            <select
              className={`${inputCls} max-w-[160px]`}
              value={examType}
              onChange={(e) => {
                setPage(1);
                setExamType(e.target.value);
              }}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t || "All types"}
                </option>
              ))}
            </select>
            <button className={`${adminButton} ml-auto`} onClick={openDeliveries}>
              <FontAwesomeIcon icon={faList} /> Delivery log
            </button>
            <button className={adminPrimaryButton} onClick={() => setUploadOpen(true)}>
              <FontAwesomeIcon icon={faUpload} /> Upload PINs
            </button>
          </div>

          {staffExamPinMutationError && (
            <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
              {staffExamPinMutationError}
            </div>
          )}
          {staffExamPinsError && (
            <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
              {staffExamPinsError}
            </div>
          )}

          {staffExamPinsLoading ? (
            <AdminLoadingState />
          ) : staffExamPins.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-400">
              No Exam PINs found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">PIN</th>
                    <th className="px-5 py-3 font-semibold">Type</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold">Sold to</th>
                    <th className="px-5 py-3 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staffExamPins.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 font-mono text-slate-800">
                        {p.pin || p.maskedPin || p.code || p.id}
                      </td>
                      <td className="px-5 py-3 text-slate-600">{p.examType || "—"}</td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                          {p.status || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        {p.soldToEmail || "—"}
                        {p.soldAt && (
                          <div className="text-[11px] text-slate-400">
                            {new Date(p.soldAt).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className={adminButton}
                            title="View PIN"
                            onClick={() => openDetail(p.id)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            className="inline-flex min-h-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                            title="Delete"
                            disabled={staffExamPinMutationLoading}
                            onClick={() => confirm({
                              title: "Delete this PIN?",
                              description: "This action cannot be undone.",
                              confirmLabel: "Delete PIN",
                              onConfirm: async () => {
                                const res = await deleteStaffExamPin(p.id);
                                if (!res.success) throw new Error(res.error || "Could not delete this PIN.");
                                await reload();
                              },
                            })}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-slate-200 p-4 text-sm text-slate-500">
            <span>
              Page {page} of {staffExamPinsPages || 1} · {staffExamPinsTotal || 0} pins
            </span>
            <div className="flex gap-2">
              <button className={adminButton} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Prev
              </button>
              <button
                className={adminButton}
                disabled={page >= (staffExamPinsPages || 1)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </AdminPanel>
      </AdminPageBody>

      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="m-0 text-lg font-semibold text-slate-900">Exam PIN details</h3>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setDetailOpen(false)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            {staffExamPinDetailLoading ? (
              <AdminLoadingState />
            ) : staffExamPinDetailError ? (
              <p className="text-sm text-red-700">{staffExamPinDetailError}</p>
            ) : staffExamPinDetail ? (
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                {[
                  ["PIN", staffExamPinDetail.pin],
                  ["Status", staffExamPinDetail.status],
                  ["Exam type", staffExamPinDetail.examType],
                  ["Sold to", staffExamPinDetail.soldToEmail],
                  ["Sold at", staffExamPinDetail.soldAt ? new Date(staffExamPinDetail.soldAt).toLocaleString() : "—"],
                  ["Created", staffExamPinDetail.createdAt ? new Date(staffExamPinDetail.createdAt).toLocaleString() : "—"],
                  ["Updated", staffExamPinDetail.updatedAt ? new Date(staffExamPinDetail.updatedAt).toLocaleString() : "—"],
                  ["Order", staffExamPinDetail.order?.reference],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
                    <dd className="m-0 mt-1 break-words text-slate-800">{value || "—"}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            <div className="mt-5 flex justify-end">
              <button className={adminButton} onClick={() => setDetailOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {deliveriesOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
          <div className="my-auto w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="m-0 text-lg font-semibold text-slate-900">Exam PIN delivery log</h3>
              <div className="flex items-center gap-2">
                <select
                  className={`${inputCls} max-w-[150px]`}
                  value={deliveryStatus}
                  onChange={(event) => {
                    setDeliveryPage(1);
                    setDeliveryStatus(event.target.value);
                  }}
                >
                  <option value="">All statuses</option>
                  <option value="SENT">SENT</option>
                  <option value="FAILED">FAILED</option>
                </select>
                <button className="text-slate-400 hover:text-slate-700" onClick={() => setDeliveriesOpen(false)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>

            {staffExamPinDeliveriesError && (
              <p className="text-sm text-red-700">{staffExamPinDeliveriesError}</p>
            )}

            {staffExamPinDeliveriesLoading ? (
              <AdminLoadingState />
            ) : staffExamPinDeliveries.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-500">No deliveries found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Recipient</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Order</th>
                      <th className="px-4 py-3 font-semibold">Attempts</th>
                      <th className="px-4 py-3 font-semibold">Sent / failed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {staffExamPinDeliveries.map((delivery) => (
                      <tr key={delivery.id}>
                        <td className="px-4 py-3 font-semibold text-slate-700">{delivery.status || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {delivery.recipient || "—"}
                          {delivery.lastError && (
                            <div className="text-xs text-red-600">{delivery.lastError}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{delivery.emailType || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">
                          {delivery.order?.reference || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{delivery.attempts ?? 0}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {delivery.sentAt ? new Date(delivery.sentAt).toLocaleString() : "—"}
                          {delivery.failedAt && (
                            <div className="text-red-600">
                              {new Date(delivery.failedAt).toLocaleString()}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>
                Page {deliveryPage} of {staffExamPinDeliveriesPages || 1} ·{" "}
                {staffExamPinDeliveriesTotal || 0} deliveries
              </span>
              <div className="flex gap-2">
                <button
                  className={adminButton}
                  disabled={deliveryPage <= 1}
                  onClick={() => setDeliveryPage((value) => value - 1)}
                >
                  Prev
                </button>
                <button
                  className={adminButton}
                  disabled={deliveryPage >= (staffExamPinDeliveriesPages || 1)}
                  onClick={() => setDeliveryPage((value) => value + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {uploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="m-0 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <FontAwesomeIcon icon={faKey} /> Upload Exam PINs
              </h3>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setUploadOpen(false)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Exam Type
            </label>
            <select className={`${inputCls} mb-3`} value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
              <option>WAEC</option>
              <option>NECO</option>
              <option>NABTEB</option>
            </select>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              PINs (one per line, or comma separated)
            </label>
            <textarea
              className={`${inputCls} h-40 font-mono`}
              value={uploadText}
              onChange={(e) => setUploadText(e.target.value)}
              placeholder={"1234567890\n0987654321"}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button className={adminButton} onClick={() => setUploadOpen(false)}>
                Cancel
              </button>
              <button className={adminPrimaryButton} disabled={staffExamPinMutationLoading} onClick={handleUpload}>
                {staffExamPinMutationLoading ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffExamPins;
